import React, { useMemo } from 'react';
import { Row, Col } from 'reactstrap';
import { useSelector } from 'react-redux';
import CustomInput from './CustomInput';
import RoleSelect from './RoleSelect';
import CategoriesMultiselect from './CategoriesMultiselect';
import CustomDatePicker from './CustomDatePicker';
import PreferredCitySelect from '../Common/PreferredCitySelect';
import { ICONS } from './icon';
import { getUserAddressCityOptions } from '../../pages/Profile/profileUser';

const dateError = (errors) =>
  [errors?.startDate, errors?.endDate].filter(Boolean).join(' ');

/**
 * OnboardingProjectForm
 * ------------------------------------------------------------------
 * Shared project entry fields for past / current / future onboarding
 * steps. Works with Formik (pass values/errors/setFieldValue) or a
 * plain draft object via the same controlled API.
 *
 * @param {object} values
 * @param {object} errors
 * @param {function} setFieldValue - (field, value) => void
 * @param {function} [onFieldBlur] - (field) => void
 * @param {array} categoryOptions
 * @param {array} [cityOptions] - optional; fetched internally when omitted
 * @param {boolean} showCity
 * @param {boolean} showDescription
 * @param {boolean} showDatePicker
 * @param {boolean} showHoursPerWeek
 * @param {'range'|'start-only'} dateMode
 * @param {string} ongoingLabel
 * @param {string} dateLabel
 * @param {string} namePlaceholder
 * @param {string} rolePlaceholder
 * @param {string} descriptionPlaceholder
 * @param {'select'|'text'} roleMode - RoleSelect vs plain text role
 */
const OnboardingProjectForm = ({
  values,
  errors = {},
  setFieldValue,
  onFieldBlur,
  categoryOptions = [],
  cityOptions: cityOptionsProp,
  showCity = true,
  showDescription = true,
  showDatePicker = true,
  showHoursPerWeek = false,
  showOngoing = true,
  dateMode = 'range',
  ongoingLabel = 'Ongoing Project',
  dateLabel = 'Project Date',
  startPlaceholder = 'Start Date',
  endPlaceholder = 'End Date',
  namePlaceholder = 'e.g. Indie Short Film',
  rolePlaceholder = 'e.g. Music Producer',
  descriptionPlaceholder = "Describe your project, its goals, and what you created.",
  roleMode = 'select',
}) => {
  const currentUser = useSelector((state) => state.auth.currentUser);

  const cityOptions = useMemo(() => {
    if (cityOptionsProp) return cityOptionsProp;
    return getUserAddressCityOptions(currentUser);
  }, [cityOptionsProp, currentUser]);

  const cityValidation = useMemo(
    () => ({
      values,
      errors,
      touched: {},
      setFieldValue,
      setFieldTouched: () => {},
    }),
    [values, errors, setFieldValue]
  );

  return (
    <div className="amok-onboarding-step__form">
      <Row>
        <Col xs={12} sm={6}>
          <CustomInput
            label="Project Name"
            placeholder={namePlaceholder}
            value={values.projectName || ''}
            onChange={(e) => setFieldValue('projectName', e.target.value)}
            onBlur={() => onFieldBlur?.('projectName')}
            error={errors.projectName}
          />
        </Col>

        <Col xs={12} sm={6}>
          {roleMode === 'select' ? (
            <RoleSelect
              label="My Role"
              placeholder={rolePlaceholder}
              value={values.roleType}
              onChange={(role) => {
                setFieldValue('roleType', role);
                setFieldValue('roleTitle', role?.name || '');
              }}
              onBlur={() => onFieldBlur?.('roleType')}
              error={errors.roleType || errors.roleTitle}
            />
          ) : (
            <CustomInput
              label="Your Role"
              placeholder={rolePlaceholder}
              value={values.role || ''}
              onChange={(e) => setFieldValue('role', e.target.value)}
              error={errors.role}
            />
          )}
        </Col>

        {showCity && (
          <Col xs={12} className="mt-3">
            <PreferredCitySelect
              validation={cityValidation}
              cityOptions={cityOptions}
              error={errors.preferredCity}
              onBlur={() => onFieldBlur?.('preferredCity')}
            />
          </Col>
        )}

        {showDescription && (
          <Col xs={12} className="mt-3">
            <CustomInput
              label="Project Description"
              placeholder={descriptionPlaceholder}
              textarea
              rows={4}
              value={values.projectDescription || ''}
              onChange={(e) =>
                setFieldValue('projectDescription', e.target.value)
              }
              onBlur={() => onFieldBlur?.('projectDescription')}
              error={errors.projectDescription}
            />
          </Col>
        )}
      </Row>

      <CategoriesMultiselect
        label="Categories"
        helperText="Select all that apply"
        options={categoryOptions}
        selected={values.categories || []}
        onChange={(next) => setFieldValue('categories', next)}
        checkIconSrc={ICONS.check}
        error={errors.categories}
      />

      {showDatePicker && (
        <CustomDatePicker
          label={dateLabel}
          startDate={values.startDate || ''}
          endDate={values.endDate || ''}
          onStartDateChange={(v) => setFieldValue('startDate', v)}
          onEndDateChange={(v) => setFieldValue('endDate', v)}
          onStartBlur={() => onFieldBlur?.('startDate')}
          onEndBlur={() => onFieldBlur?.('endDate')}
          isOngoing={showOngoing ? !!values.isOngoing : false}
          onOngoingChange={
            dateMode === 'range' && showOngoing
              ? (v) => {
                  setFieldValue('isOngoing', v);
                  if (v) setFieldValue('endDate', '');
                }
              : undefined
          }
          ongoingLabel={ongoingLabel}
          showEndDate={dateMode === 'range'}
          startPlaceholder={
            dateMode === 'start-only' ? 'Target Date' : startPlaceholder
          }
          endPlaceholder={endPlaceholder}
          calendarIconSrc={ICONS.calendar}
          checkIconSrc={ICONS.check}
          error={dateError(errors)}
        />
      )}

      {showHoursPerWeek && (
        <CustomInput
          label="Hours per Week"
          placeholder="e.g. 15"
          inputMode="numeric"
          value={values.hoursPerWeek || ''}
          onChange={(e) => setFieldValue('hoursPerWeek', e.target.value)}
          error={errors.hoursPerWeek}
        />
      )}
    </div>
  );
};

export default OnboardingProjectForm;
